import { useCallback, useMemo, useState } from 'react';

import { defaultWorkflowFileSort } from '@aline/common';
import { TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  SelectiveKeyboardSensor,
  SelectiveMouseSensor,
  SelectivePointerSensor,
} from 'src/components/workflow/board/utils/keyboardCoordinateGetter';
import { Apollo } from 'src/gql/generated';
import { useGetWorkflowFiles, useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

import { BLOCKED_SIGNING_DROP_MESSAGE } from '../document-panel-messages';

const SECTION_ROLES = [
  Apollo.WorkflowFileDocumentRole.General,
  Apollo.WorkflowFileDocumentRole.Signing,
] as const;

function getIsSectionRole(id: unknown): id is Apollo.WorkflowFileDocumentRole {
  return typeof id === 'string' && SECTION_ROLES.includes(id as Apollo.WorkflowFileDocumentRole);
}

export function useDocumentDnd(workflowId: UUID) {
  const dispatch = useNotificationDispatch();
  const { loading, workflowFiles, workflowWithFiles } = useGetWorkflowFiles(workflowId);
  const { data: phasesData } = Apollo.useGetWorkflowPhasesQuery({ variables: { workflowId } });
  const hasSigningPhase =
    phasesData?.workflow?.phases?.some(p => p.__typename === 'SigningPhase') ?? false;
  const [setWorkflowFileDocumentRole] = Apollo.useSetWorkflowFileDocumentRoleMutation();
  const [updateWorkflowFileOrder] = Apollo.useUpdateWorkflowFileOrderMutation();

  const sensors = useSensors(
    useSensor(SelectivePointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(SelectiveKeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    useSensor(SelectiveMouseSensor, { activationConstraint: { distance: 8 } })
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overContainerId, setOverContainerId] = useState<Apollo.WorkflowFileDocumentRole | null>(
    null
  );

  const generalFiles = useMemo(
    () =>
      [...workflowFiles]
        .filter(f => f.documentRole === Apollo.WorkflowFileDocumentRole.General)
        .sort(defaultWorkflowFileSort),
    [workflowFiles]
  );

  const signingFiles = useMemo(
    () =>
      [...workflowFiles]
        .filter(f => f.documentRole === Apollo.WorkflowFileDocumentRole.Signing)
        .sort(defaultWorkflowFileSort),
    [workflowFiles]
  );

  const sortableSigningIds = useMemo(
    () => signingFiles.filter(f => f.orderId !== 'certificate').map(f => f.id),
    [signingFiles]
  );

  const activeFile = useMemo(
    () => workflowFiles.find(f => f.id === activeId) ?? null,
    [workflowFiles, activeId]
  );

  /**
   * Both drop mutations run un-awaited so the overlay can come down on drop, which means
   * nothing else is left to surface a rejection — the server refuses some moves outright
   * (a document with fields can't become general).
   */
  const notifyMoveFailed = useCallback(
    (error: unknown) => {
      dispatch({
        payload: {
          key: uuid4(),
          message: error instanceof Error ? error.message : "Couldn't move that document.",
          variant: 'error',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    },
    [dispatch]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over?.id) return;

      const draggedId = String(active.id);
      const overId = String(over.id);

      // Reorder within signing: both IDs are sortable signing file IDs.
      const oldIdx = sortableSigningIds.indexOf(draggedId);
      const newIdx = sortableSigningIds.indexOf(overId);
      if (oldIdx !== -1 && newIdx !== -1) {
        if (oldIdx !== newIdx) {
          const orderId = newIdx + 1;
          /**
           * Don't await — optimisticResponse updates the cache synchronously, and waiting
           *  on the network would leave the drag overlay stuck until the server replies.
           */
          void updateWorkflowFileOrder({
            optimisticResponse: {
              __typename: 'Mutation',
              updateWorkflowFileOrder: getReorderedFiles(signingFiles, draggedId, orderId),
            },
            variables: { input: { fileId: draggedId, orderId } },
          }).catch(notifyMoveFailed);
        }
        return;
      }

      // Move between roles: resolve target role from section backdrop OR a file row inside it.
      const targetRole = getIsSectionRole(over.id)
        ? over.id
        : (workflowFiles.find(f => f.id === overId)?.documentRole ?? null);

      if (!targetRole) return;
      if (targetRole === Apollo.WorkflowFileDocumentRole.Signing && !hasSigningPhase) {
        dispatch({
          payload: {
            key: uuid4(),
            message: BLOCKED_SIGNING_DROP_MESSAGE,
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }
      const file = workflowFiles.find(f => f.id === draggedId);
      if (!file || file.documentRole === targetRole) return;
      // Awaiting here would hold the overlay on screen for the whole round trip.
      void setWorkflowFileDocumentRole({
        variables: { input: { documentRole: targetRole, workflowFileId: file.id } },
      }).catch(notifyMoveFailed);
    },
    [
      signingFiles,
      sortableSigningIds,
      workflowFiles,
      hasSigningPhase,
      dispatch,
      notifyMoveFailed,
      updateWorkflowFileOrder,
      setWorkflowFileDocumentRole,
    ]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const overId = event.over?.id ? String(event.over.id) : null;
      if (!overId) {
        setOverContainerId(null);
        return;
      }
      const role = getIsSectionRole(overId)
        ? overId
        : (workflowFiles.find(f => f.id === overId)?.documentRole ?? null);
      if (role === Apollo.WorkflowFileDocumentRole.Signing && !hasSigningPhase) {
        setOverContainerId(null);
        return;
      }
      setOverContainerId(role);
    },
    [workflowFiles, hasSigningPhase]
  );

  const clearDragState = useCallback(() => {
    setActiveId(null);
    setOverContainerId(null);
  }, []);

  const dndHandlers = useMemo(
    () => ({
      onDragCancel: clearDragState,
      onDragEnd: (e: DragEndEvent) => {
        /**
         * Kick off the mutation first — its optimisticResponse writes the new order
         * synchronously — then clear drag state. Reversing that leaves a frame where
         * the overlay is gone and rows animate back to their pre-drag positions.
         */
        void handleDragEnd(e).finally(clearDragState);
      },
      onDragOver: handleDragOver,
      onDragStart: (e: DragStartEvent) => {
        setActiveId(String(e.active.id));
        setOverContainerId(null);
      },
    }),
    [clearDragState, handleDragEnd, handleDragOver]
  );

  return {
    activeFile,
    activeId,
    dndHandlers,
    generalFiles,
    loading,
    overContainerId,
    sensors,
    signingFiles,
    sortableSigningIds,
    workflowFiles,
    workflowWithFiles,
  };
}

/**
 * The reorder the server is about to perform: the dragged file takes `orderId` and every
 * file it moved past shifts one slot the other way. Sent as the optimistic response so the
 * list settles into its new order on drop — otherwise the rows animate back to their old
 * order and only jump once the mutation resolves.
 */
function getReorderedFiles(
  files: Pick<Apollo.WorkflowFile, 'id' | 'orderId'>[],
  fileId: string,
  orderId: number
): Apollo.UpdateWorkflowFileOrderMutation['updateWorkflowFileOrder'] {
  const draggedFile = files.find(f => f.id === fileId);
  if (!draggedFile) return [];

  const fromOrderId = parseInt(draggedFile.orderId);
  const isMovingForward = orderId > fromOrderId;
  // The certificate's non-numeric orderId makes every comparison false, so it never shifts.
  const shiftedFiles: Apollo.UpdateWorkflowFileOrderMutation['updateWorkflowFileOrder'] =
    files.flatMap(file => {
      if (file.id === fileId) return [];
      const fileOrderId = parseInt(file.orderId);
      const isImpacted = isMovingForward
        ? fileOrderId > fromOrderId && fileOrderId <= orderId
        : fileOrderId < fromOrderId && fileOrderId >= orderId;
      if (!isImpacted) return [];
      return [
        {
          __typename: 'WorkflowFile',
          id: file.id,
          orderId: `${fileOrderId + (isMovingForward ? -1 : 1)}`,
        },
      ];
    });

  return [...shiftedFiles, { __typename: 'WorkflowFile', id: fileId, orderId: `${orderId}` }];
}
